/**
 * ArtefactsList Component
 *
 * Displays artefacts in a single column with inline viewer.
 * Shows artefacts in an accordion layout within tabs.
 */

import type { Selection } from "@heroui/react";

import React, { useRef, useEffect } from "react";
import { Chip, Accordion, AccordionItem } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";

import { useArtefactStore } from "@/stores/ArtefactStore";
import { Artefact, ARTEFACT_TYPE_META, ArtefactType } from "@/types/artefacts";
import { parseTableContent, getTableDimensions, getTablePreview } from "@/utils/tableUtils";
import {
  generateDocumentMetadata,
  generateDocumentPreview,
  formatWordCount,
  formatReadingTime,
} from "@/utils/documentUtils";

import { ArtefactViewer } from "./ArtefactViewer";

/**
 * Strip markdown formatting from text
 */
const stripMarkdown = (text: string): string => {
  return text
    .replace(/#{1,6}\s+/g, "") // Remove heading markers
    .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold
    .replace(/\*(.*?)\*/g, "$1") // Remove italic
    .replace(/`(.*?)`/g, "$1") // Remove inline code
    .replace(/\[(.*?)\]\(.*?\)/g, "$1") // Remove links, keep text
    .replace(/!\[(.*?)\]\(.*?\)/g, "$1") // Remove images, keep alt text
    .replace(/>/g, "") // Remove blockquote
    .replace(/[-*+]\s+/g, "") // Remove list markers
    .replace(/\d+\.\s+/g, "") // Remove numbered list markers
    .trim();
};

export const ArtefactsList: React.FC = () => {
  const { getFilteredArtefactsForCurrentChat, selectedArtefactIds, selectMultipleArtefacts } =
    useArtefactStore();

  const artefacts = getFilteredArtefactsForCurrentChat();
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to selected artefacts when selection changes externally
  useEffect(() => {
    if (selectedArtefactIds.length === 0 || !containerRef.current) return;

    // Wait for accordion animation to complete
    const timer = setTimeout(() => {
      const firstSelectedId = selectedArtefactIds[0];

      // Find the accordion item by its data-key attribute (set by HeroUI)
      const accordionItem = containerRef.current?.querySelector(`[data-key="${firstSelectedId}"]`);

      if (accordionItem) {
        accordionItem.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 350); // Wait for accordion animation (300ms) + buffer

    return () => clearTimeout(timer);
  }, [selectedArtefactIds]);

  // Handle accordion selection changes from user interaction
  const handleSelectionChange = (keys: Selection) => {
    if (keys === "all") {
      selectMultipleArtefacts(artefacts.map((a) => a.id));
    } else {
      selectMultipleArtefacts(Array.from(keys) as string[]);
    }
  };

  const formatRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);

    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);

    return `${diffDays}d ago`;
  };

  const getArtefactPreview = (artefact: Artefact): string => {
    switch (artefact.type) {
      case ArtefactType.TABLE: {
        const tableData = parseTableContent(artefact.content);

        if (tableData) {
          return getTablePreview(tableData.headers, tableData.rows);
        }
        break;
      }
      case ArtefactType.DOCUMENT: {
        return generateDocumentPreview(artefact.content);
      }
      case ArtefactType.HTML: {
        // Show user-friendly preview for HTML artefacts instead of raw HTML code
        const isChart =
          artefact.title.toLowerCase().includes("chart") ||
          artefact.content.includes("canvas") ||
          artefact.content.includes("Chart");

        return isChart ? "📊 Interactive Chart" : "🌐 HTML Content";
      }
      default:
        break;
    }

    // Fallback for other types
    const maxLength = 100;
    const content = artefact.content.trim();

    if (content.length <= maxLength) return content;

    return content.substring(0, maxLength) + "...";
  };

  const getArtefactMetadata = (artefact: Artefact): string => {
    const parts: string[] = [];

    switch (artefact.type) {
      case ArtefactType.TABLE: {
        const tableData = parseTableContent(artefact.content);

        if (tableData) {
          parts.push(getTableDimensions(tableData));
        }
        break;
      }
      case ArtefactType.DOCUMENT: {
        const metadata = generateDocumentMetadata(artefact.content);

        parts.push(formatWordCount(metadata.wordCount));
        parts.push(formatReadingTime(metadata.readingTime));
        break;
      }
      default:
        break;
    }

    // Add estimated size if available
    if (artefact.metadata?.estimatedSize) {
      parts.push(artefact.metadata.estimatedSize);
    }

    // Add timestamp
    parts.push(formatRelativeTime(artefact.timestamp));

    return parts.join(" • ");
  };

  if (artefacts.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center text-default-500">
          <div className="text-4xl mb-3">🎨</div>
          <p className="text-sm">No artefacts detected yet</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full overflow-y-auto px-4 py-3">
      <Accordion
        className="gap-2"
        selectedKeys={selectedArtefactIds}
        selectionMode="multiple"
        onSelectionChange={handleSelectionChange}
      >
        {artefacts.map((artefact) => {
          const typeMeta = ARTEFACT_TYPE_META[artefact.type];

          return (
            <AccordionItem
              key={artefact.id}
              aria-label={artefact.title}
              subtitle={
                <p className="text-xs text-default-600 line-clamp-2 ml-11">
                  {getArtefactPreview(artefact)}
                </p>
              }
              title={
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-default-100 ${typeMeta.color}`}>
                    <Icon height={16} icon={typeMeta.icon} width={16} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-default-800">
                        {stripMarkdown(artefact.title)}
                      </span>
                      <Chip className="text-xs" color="default" size="sm" variant="flat">
                        {typeMeta.label}
                      </Chip>
                    </div>
                    <div className="text-xs text-default-500">{getArtefactMetadata(artefact)}</div>
                  </div>
                </div>
              }
            >
              <div className="p-4 bg-default-50 dark:bg-default-100 rounded-lg">
                <ArtefactViewer artefact={artefact} />
              </div>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};
